import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPublicMenus } from "../utils/api";
import { FaPlus, FaMinus, FaChevronDown, FaShoppingCart, FaSearch } from "react-icons/fa";
import { motion } from "framer-motion";

// Helper to get full image URL
const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;
function getFullImageUrl(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http")) return imageUrl;
  const cleanUrl = imageUrl.replace(/^\/?.*uploads\//, "");
  return `${BASE_URL}/uploads/${cleanUrl}`;
}

export default function CustomerTable() {
  const { restaurantId, tableId } = useParams();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState([]);
  // Replace category accordion with selectedMenuId + search
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchMenus();
    // Load cart from localStorage if present
    const stored = localStorage.getItem(`cart_${restaurantId}_${tableId}`);
    setCart(stored ? JSON.parse(stored) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, tableId]);

  useEffect(() => {
    localStorage.setItem(
      `cart_${restaurantId}_${tableId}`,
      JSON.stringify(cart)
    );
  }, [cart, restaurantId, tableId]);

  async function fetchMenus() {
    setLoading(true);
    setError("");
    try {
      const data = await getPublicMenus(restaurantId);
      setMenus(data);
      if (data && data.length > 0) {
        setSelectedMenuId((prev) => prev || data[0].id);
      }
    } catch (err) {
      setError("Failed to load menu");
    }
    setLoading(false);
  }

  function addToCart(item) {
    setCart((prev) => {
      const found = prev.find((i) => i.id === item.id);
      const updated = found
        ? prev.map((i) => (i.id === item.id ? { ...i, qty: i.qty + 1 } : i))
        : [...prev, { ...item, qty: 1 }];
      return updated;
    });
  }

  function incrementQty(itemId) {
    setCart((prev) => {
      const updated = prev.map((i) =>
        i.id === itemId ? { ...i, qty: i.qty + 1 } : i
      );
      return updated;
    });
  }

  function decrementQty(itemId) {
    setCart((prev) => {
      const found = prev.find((i) => i.id === itemId);
      let updated;
      if (found && found.qty === 1) {
        updated = prev.filter((i) => i.id !== itemId);
      } else {
        updated = prev.map((i) =>
          i.id === itemId ? { ...i, qty: i.qty - 1 } : i
        );
      }
      return updated;
    });
  }

  function handleViewCart() {
    localStorage.setItem(
      `cart_${restaurantId}_${tableId}`,
      JSON.stringify(cart)
    );
    navigate(`/cart?restaurantId=${restaurantId}&tableId=${tableId}`);
  }

  // derive filtered items and totals
  const lowerSearch = searchTerm.trim().toLowerCase();
  let filteredItems = [];
  if (!lowerSearch) {
    const sel = menus.find((m) => m.id === selectedMenuId);
    filteredItems = (sel?.items || []).filter((i) => i.active !== false).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  } else {
    filteredItems = menus
      .flatMap((m) => (m.items || []).filter((i) => i.active !== false))
      .filter((i) => i.name.toLowerCase().includes(lowerSearch))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  const totalItems = cart.reduce((s, it) => s + it.qty, 0);
  const subtotal =
    Math.round(
      cart.reduce((s, it) => s + Number(it.price || 0) * it.qty, 0) * 100
    ) / 100;
  const tax = Math.round(subtotal * 0.05 * 100) / 100;
  const grandTotal = Math.round((subtotal + tax) * 100) / 100;

  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center p-8"
        >
          <div className="inline-block relative w-16 h-16">
            <motion.div
              className="absolute inset-0 border-4 border-rose-400 border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="mt-4 text-rose-800 font-medium">Loading menu...</p>
        </motion.div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-6 rounded-xl shadow-lg max-w-md mx-4 text-center"
        >
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Oops!</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchMenus}
            className="px-4 py-2 bg-gradient-to-r from-rose-400 to-amber-400 text-white rounded-lg font-medium hover:shadow-md transition-all"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-2 md:p-8 flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full mx-auto bg-white rounded-2xl shadow-lg overflow-hidden p-2 md:p-6 flex-1 flex flex-col"
      >
        <div className="relative mb-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
            Menu
          </h2>
        </div>

        {/* Search + categories */}
        <div className="px-4">
          <div className="relative mb-3">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>

          {menus.length > 0 && (
            <div className="mb-3">
              <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                {menus.map((menu) => (
                  <button
                    key={menu.id}
                    onClick={() => {
                      setSelectedMenuId(menu.id);
                      setSearchTerm("");
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                      selectedMenuId === menu.id ? "bg-rose-500 text-white" : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {menu.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Items grid */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {filteredItems.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {menus.length === 0
                ? "No menu categories available"
                : searchTerm
                ? `No items found for "${searchTerm}"`
                : "No items in this category"}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {filteredItems.map((item) => {
                const cartItem = cart.find((c) => c.id === item.id);
                return (
                  <div key={item.id} className="bg-white rounded-lg p-2 border border-gray-200 flex flex-col">
                    {item.imageUrl && (
                      <img
                        src={getFullImageUrl(item.imageUrl)}
                        alt={item.name}
                        className="w-full h-20 object-cover rounded mb-2"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <div className="text-xs font-semibold text-center mb-1 truncate">{item.name}</div>
                    <div className="text-rose-600 font-bold text-center mb-2">₹{item.price || 0}</div>

                    <div className="mt-auto">
                      {cartItem ? (
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => decrementQty(item.id)}
                            className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"
                          >
                            <FaMinus size={12} />
                          </button>
                          <span className="font-semibold">{cartItem.qty}</span>
                          <button
                            onClick={() => incrementQty(item.id)}
                            className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"
                          >
                            <FaPlus size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => addToCart(item)}
                          className="w-full py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg text-sm font-semibold"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* View Cart Button placed after all categories, responsive */}
      <div className="w-full flex justify-center mt-6 mb-8 md:mb-0">
        <motion.button
          className={`btn w-full max-w-xs flex items-center justify-between gap-2 text-lg py-3 shadow-lg rounded-full relative overflow-hidden px-4 ${
            cart.length === 0
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-rose-500 to-amber-500 text-white hover:shadow-xl"
          }`}
          onClick={handleViewCart}
          disabled={cart.length === 0}
          style={{ minHeight: 48 }}
          whileHover={cart.length > 0 ? { scale: 1.02 } : {}}
          whileTap={cart.length > 0 ? { scale: 0.98 } : {}}
        >
          <div className="flex items-center gap-3">
            <FaShoppingCart />
            <span>View Cart</span>
          </div>

          <div className="text-right">
            <div className="text-sm font-medium">{totalItems} item{totalItems !== 1 ? "s" : ""}</div>
            <div className="text-sm">₹{grandTotal.toFixed(2)}</div>
          </div>
        </motion.button>
      </div>

      <motion.div
        className="text-center text-xs text-gray-400 mt-8 mb-16 md:mb-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        Powered by <span className="font-bold text-rose-400">EasiDine</span>
      </motion.div>
    </div>
  );
}
